import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../connector/api';

export default function RequestReset() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailError, setEmailError] = useState(''); // Stores validation error message

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(false);
        setEmailError(''); // Clear any previous errors

        setLoading(true);
        try {
            const res = await api.post('password-reset/', { email });
            toast.success(res.data.detail || "Reset link sent successfully!");
            setEmail('');
        } catch (err) {
            // Check if the backend returned a 404 Not Found
            if (err.response && err.response.status === 404) {
                setEmailError(err.response.data.error || "This email address is not linked to any user.");
            } else {
                toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center font-dynamic-title px-4 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-950 dark:to-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-300">
            <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700/50 p-4 md:p-6 transform transition-all backdrop-blur-[6px]">
                
                {/* Header */}
                <div className="text-center mb-6">
                    <div className="mx-auto h-12 w-12 rounded-xl bg-[#002855] dark:bg-blue-600 flex items-center justify-center mb-4 shadow-md shadow-blue-900/20">
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h2 className="text-sm font-bold uppercase text-[#002855] text-opacity-80 dark:text-blue-400 tracking-tight">Forgot Password</h2>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-2">
                        Enter your email to receive a password recovery link.
                    </p>
                </div>

                <form className="space-y-4" onSubmit={handleSubmit}>
                    {/* Email Field Wrapper */}
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-2">
                            Email Address
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (emailError) setEmailError(''); // Clear error immediately when user alters typing
                            }}
                            className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border rounded-xl focus:outline-none focus:ring-2 transition duration-200 text-sm placeholder-gray-400 dark:placeholder-gray-500 ${
                                emailError 
                                    ? 'border-red-500 focus:ring-red-500 text-red-900 dark:text-red-200 bg-red-50/50 dark:bg-red-950/20' 
                                    : 'border-gray-200 dark:border-gray-700 focus:ring-[#002855] dark:focus:ring-blue-500 focus:border-transparent'
                            }`}
                            placeholder="name@school.com"
                            required
                        />
                        
                        {/* Dynamic Inline Error Message */}
                        {emailError && (
                            <p className="text-red-500 text-xs mt-2 font-medium flex items-center gap-1 animate-fadeIn">
                                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                {emailError}
                            </p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full relative mt-2 py-3 px-4 text-xs font-semibold text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 dark:bg-blue-600 dark:hover:bg-blue-700 rounded-xl transition duration-200 shadow-md active:scale-[0.99] focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            loading ? "opacity-75 cursor-not-allowed" : ""
                        }`}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center space-x-2">
                                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                <span>Verifying...</span>
                            </span>
                        ) : (
                            "Send Reset Link"
                        )}
                    </button>
                </form>

                {/* Back to Login Link */}
                <div className="text-center mt-6 pt-4 border-t border-gray-100 dark:border-gray-700/50">
                    <Link to="/login" className="text-xs font-semibold text-[#002855] dark:text-blue-400 hover:underline">
                        Back to Sign In
                    </Link>
                </div>

            </div>
        </div>
    );
}